/**
 * @documental
 * @export Paragraph
 * @import
 * import { Paragraph } from "capitalrx-components-2.0/lib/typography";
 */
import { FC, PropsWithChildren } from "react";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { TextAlignment } from "../TextAlignment";

import tokenStyles from "./styles.module.css";

/**
 * The Paragraph component properties.
 */
export interface ParagraphProps extends BaseProps {
  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;
}

const useResolvedParagraphStyles = createComponentStyleHook<ParagraphProps>({
  legacyStyles: () => import("./styles.module.scss"),
  tokenStyles,
  componentTokens: "paragraph",
});

/**
 * Specifies that the text content should semantically be placed within a paragraph. Paragraphs are automatically spaced apart from
 * their siblings. However, if the paragraph is the first element it will not have any spacing above, and if it is the last element
 * it will not have any spacing below.
 */
export const Paragraph: FC<PropsWithChildren<ParagraphProps>> = (props) => {
  const { children, textAlign = "left" } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedParagraphStyles(props);
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.Paragraph, "paragraph", `align-${textAlign}`] });

  return (
    <StyleModeBoundary mode={mode}>
      <p {...baseHtmlProps}>{children}</p>
    </StyleModeBoundary>
  );
};
